import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    getConsent,
    setConsent,
    onConsentChange,
    CONSENT_ACCEPTED,
    CONSENT_DECLINED,
} from "@/lib/consent";

// Slim editorial consent card, bottom-right. Only rendered when the user is
// still undecided — no impact on Core Web Vitals for accepted/declined users.
export default function ConsentBanner() {
    const [visible, setVisible] = useState(false);
    const [entered, setEntered] = useState(false);

    useEffect(() => {
        // Defer to next frame so it doesn't compete with LCP on first paint.
        const raf = requestAnimationFrame(() => {
            if (getConsent() == null) setVisible(true);
        });
        const off = onConsentChange((v) => setVisible(v == null));
        return () => {
            cancelAnimationFrame(raf);
            off();
        };
    }, []);

    useEffect(() => {
        if (visible) {
            const t = requestAnimationFrame(() => setEntered(true));
            return () => cancelAnimationFrame(t);
        }
        setEntered(false);
    }, [visible]);

    if (!visible) return null;

    return (
        <div
            data-testid="consent-banner"
            className={`fixed bottom-4 right-4 left-4 sm:left-auto z-[70] sm:w-[340px] transition-all duration-300 ease-out ${
                entered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
            }`}
            role="dialog"
            aria-live="polite"
            aria-label="Cookie preferences"
        >
            <div className="bg-obsidian text-bone/90 border border-bone/10 p-5 shadow-2xl">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-olive-mint mb-3">
                    · Cookies
                </div>
                <p className="text-[13px] leading-relaxed text-bone/80">
                    We use cookies for anonymous analytics (Google Analytics &
                    Microsoft Clarity) to understand how the site is used and
                    make it better.
                </p>
                <Link
                    data-testid="consent-privacy-link"
                    to="/privacy"
                    className="mt-3 inline-block text-[12px] text-olive-mint hover:text-bone underline underline-offset-4 decoration-olive-mint/50 hover:decoration-bone transition-colors duration-200"
                >
                    Read our Privacy Policy →
                </Link>
                <div className="mt-5 flex items-center gap-2">
                    <button
                        data-testid="consent-decline"
                        onClick={() => setConsent(CONSENT_DECLINED)}
                        className="text-[11px] uppercase tracking-[0.18em] font-bold text-bone/60 hover:text-bone px-3 py-2.5 transition-colors duration-200"
                    >
                        Decline
                    </button>
                    <button
                        data-testid="consent-accept"
                        onClick={() => setConsent(CONSENT_ACCEPTED)}
                        className="text-[11px] uppercase tracking-[0.18em] font-bold bg-olive text-obsidian px-5 py-2.5 hover:bg-bone hover:text-obsidian transition-colors duration-200 ml-auto"
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
}
